import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  BodyText,
  theme,
  useContainerSpacing,
  useResponsive,
  VStack,
  webOnlyStyles,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../context/AuthMachine';
import { useCustomer } from '../../../context/Customer';
import { useTrademark } from '../../../hooks';
import { resetStacksPostLogout } from '../../../screens/AccountScreen/AccountScreen.navigation';
import { DebugMenuTrigger } from '../../DebugMenu';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';
import { AccountCustomerName } from './components/AccountCustomerName';
import { AccountMenuCrashOptions } from './components/AccountMenuCrashOptions';
import { AccountMenuSignOutItem } from './components/AccountMenuSignOutItem';
import { AccountPrimaryMenu } from './components/AccountPrimaryMenu';
import { AccountSecondaryMenu } from './components/AccountSecondaryMenu';

import type { AccountTabScreenId } from '../../../navigation/AppNavigation.props';

export const AccountMenu = (props: AccountMenuProps) => {
  const { screen: screenId } = props;
  const { currentBreakpoint, match } = useResponsive();
  const { isXS } = currentBreakpoint;
  const { isSigningOut } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const isCrashMenuEnabled = useFeatureFlag('permanent-order-crash-option-enabled');

  const trademark = useTrademark();
  const a11ySelector = useA11ySelector('account.links');

  // ─── Menu Styles ─────────────────────────────────────────────────────

  const dividerColor = isXS ? theme.colors.DARK_KALE : theme.colors.LIGHT;
  const menuHorizontalSpacing = useContainerSpacing();
  const menuDividerSpacing = match([24, 32]);
  const menuContainerStyle = [
    useStyle(
      () => ({
        paddingVertical: match([40, 48]),
        paddingHorizontal: isXS ? 0 : menuHorizontalSpacing,
      }),
      [isXS, menuHorizontalSpacing],
    ),
    isXS ? null : menuContainerWebStyle,
  ];

  // ─── Name Styles ─────────────────────────────────────────────────────

  const nameSpacing = match([20, 16]);
  const nameContainerStyle = useStyle(
    () => ({
      paddingBottom: nameSpacing,
    }),
    [nameSpacing],
  );

  // ─── Logged In ───────────────────────────────────────────────────────

  if (isLoggedIn) {
    return (
      <VStack
        testID="account-menu"
        style={menuContainerStyle}
        gap={menuDividerSpacing}
        hasDivider
        dividerColor={dividerColor}
        {...a11ySelector.props}
      >
        <View style={nameContainerStyle}>
          <AccountCustomerName />
        </View>

        <AccountPrimaryMenu
          screenId={screenId}
          gap={match([theme.spacing['4'], 0])}
          hasDivider={isXS}
          dividerColor={theme.colors.LIGHT}
        />

        {Platform.OS === 'web' ? null : (
          <VStack gap={0}>
            <AccountSecondaryMenu />
          </VStack>
        )}

        <VStack gap={0}>
          <AccountMenuSignOutItem
            isSigningOut={isSigningOut}
            resetNavigation={resetStacksPostLogout}
          />
        </VStack>

        <VStack style={styles.menuFooterSpacing} gap={0}>
          {Platform.OS !== 'web' && isXS && trademark ? (
            <BodyText style={styles.trademarkText}>{trademark}</BodyText>
          ) : null}

          {isCrashMenuEnabled ? <AccountMenuCrashOptions /> : null}

          <DebugMenuTrigger />
        </VStack>
      </VStack>
    );
  }

  // ─── Logged Out ──────────────────────────────────────────────────────

  return (
    <VStack
      testID="account-menu"
      style={menuContainerStyle}
      gap={menuDividerSpacing}
      hasDivider
      dividerColor={dividerColor}
    >
      <AccountSignedOutMessage />

      {Platform.OS === 'web' ? null : (
        <VStack gap={0}>
          <AccountSecondaryMenu />
        </VStack>
      )}

      <VStack style={styles.menuFooterSpacing} gap={0}>
        {Platform.OS !== 'web' && isXS && trademark ? (
          <BodyText style={styles.trademarkText}>{trademark}</BodyText>
        ) : null}
        {isCrashMenuEnabled ? <AccountMenuCrashOptions /> : null}
        <DebugMenuTrigger />
      </VStack>
    </VStack>
  );
};

type AccountMenuProps = Readonly<{
  screen?: AccountTabScreenId;
}>;

const styles = StyleSheet.create({
  menuFooterSpacing: {
    marginTop: theme.spacing['6'],
  },
  trademarkText: {
    marginBottom: theme.spacing['4'],
  },
});

const menuContainerWebStyle = webOnlyStyles({
  position: 'sticky',
  top: 0,
} as unknown as Parameters<typeof webOnlyStyles>[0]);
